"use client";

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/utils/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ColorPicker } from '@/components/ColorPicker';
import { Globe, Palette, Shield, Zap, Download, Upload, AlertCircle } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { showSuccess, showError } from '@/utils/toast';
import { Skeleton } from '@/components/ui/skeleton';

const CompanyManagement = () => {
  const { profile, company } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: company?.name || '',
    subdomain: company?.subdomain || '',
    primary_color: company?.primary_color || '#3b82f6',
    secondary_color: company?.secondary_color || '#6b7280',
    logo_url: company?.logo_url || '',
    status: company?.status || 'active',
  });

  const { data: companyData, isLoading } = useQuery({
    queryKey: ['company', company?.id],
    queryFn: async () => {
      if (!company?.id) return null;
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', company.id)
        .single();
      if (error) {
        if (error.message.includes('permission')) {
          showError('Access denied. Contact superadmin.');
          return null;
        }
        throw error;
      }
      return data;
    },
    enabled: !!company?.id && profile?.role === 'admin',
    retry: (failureCount, error: any) => failureCount < 3 && !error.message.includes('permission'),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!company?.id) throw new Error('No company');
      const { error } = await supabase
        .from('companies')
        .update(data)
        .eq('id', company.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] });
      setEditing(false);
      showSuccess('Company updated!');
    },
    onError: (error: any) => {
      if (error.message.includes('permission')) {
        showError('Access denied. Contact superadmin.');
      } else {
        showError('Failed to update company');
      }
    },
  });

  const verifyDNSMutation = useMutation({
    mutationFn: async (domain: string) => {
      const { data, error } = await supabase.functions.invoke('verify-dns', {
        body: { domain },
      });
      if (error) throw new Error((error as any).message || 'DNS verification failed');
      if (!data?.valid) throw new Error((data as any)?.error || 'DNS verification failed');
      return data;
    },
    onSuccess: (result: any) => {
      showSuccess(`DNS verified! Record: ${result.record}`);
      updateMutation.mutate({ domain_verified: true });
    },
    onError: (error: any) => {
      showError(error.message || 'DNS verification failed');
    },
  });

  const uploadLogoMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!company?.id) throw new Error('No company');
      const { data, error } = await supabase.storage
        .from('company-logos')
        .upload(`${company.id}/${Date.now()}-${file.name}`, file, {
          cacheControl: '3600',
          upsert: false,
        });
      if (error) throw error;
      const publicUrl = supabase.storage.from('company-logos').getPublicUrl(data!.path).data.publicUrl;
      return publicUrl;
    },
    onSuccess: (url: string) => {
      updateMutation.mutate({ logo_url: url });
      showSuccess('Logo uploaded!');
    },
    onError: () => {
      showError('Failed to upload logo');
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleDNSVerify = () => {
    if (!formData.subdomain) {
      showError('Enter subdomain first');
      return;
    }
    const domain = `${formData.subdomain}.wightwayconnect.uk`;
    verifyDNSMutation.mutate(domain);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadLogoMutation.mutate(file);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Company Management</h1>
        <Button onClick={() => setEditing(!editing)}>
          {editing ? 'Cancel' : 'Edit Company'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Company Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {editing ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Company Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subdomain">Subdomain</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="subdomain"
                      value={formData.subdomain}
                      onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                      placeholder="yourcompany"
                    />
                    <span className="text-muted-foreground">.wightwayconnect.uk</span>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={handleDNSVerify} disabled={verifyDNSMutation.isPending}>
                    {verifyDNSMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Globe className="h-4 w-4 mr-2" />}
                    Verify DNS
                  </Button>
                  {companyData?.domain_verified && <Badge>Verified</Badge>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_color">Primary Color</Label>
                  <ColorPicker
                    value={formData.primary_color}
                    onChange={(color) => setFormData({ ...formData, primary_color: color })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondary_color">Secondary Color</Label>
                  <ColorPicker
                    value={formData.secondary_color}
                    onChange={(color) => setFormData({ ...formData, secondary_color: color })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="logo">Company Logo</Label>
                <div className="flex space-x-2">
                  <Input
                    id="logo"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="flex-1"
                  />
                  {uploadLogoMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                </div>
                {companyData?.logo_url && (
                  <img src={companyData.logo_url} alt="Logo" className="h-12 w-auto mt-2" />
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="status"
                  checked={formData.status === 'active'}
                  onCheckedChange={(checked) => setFormData({ ...formData, status: checked ? 'active' : 'inactive' })}
                />
                <Label htmlFor="status">Active Status</Label>
              </div>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Shield className="mr-2 h-4 w-4" />}
                {updateMutation.isPending ? 'Updating...' : 'Update Company'}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-medium">Company Name</div>
                  <div>{companyData?.name}</div>
                </div>
                <div>
                  <div className="font-medium">Subdomain</div>
                  <div>{companyData?.subdomain}.wightwayconnect.uk</div>
                  {companyData?.domain_verified && <Badge variant="default">Custom Domain Verified</Badge>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-medium">Primary Color</div>
                  <div className="w-8 h-8 rounded" style={{ backgroundColor: companyData?.primary_color }}></div>
                </div>
                <div>
                  <div className="font-medium">Secondary Color</div>
                  <div className="w-8 h-8 rounded" style={{ backgroundColor: companyData?.secondary_color }}></div>
                </div>
              </div>
              <div>
                <div className="font-medium">Logo</div>
                {companyData?.logo_url ? (
                  <img src={companyData.logo_url} alt="Logo" className="h-12 w-auto" />
                ) : (
                  <div className="text-muted-foreground">No logo uploaded</div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <div className="font-medium">Status</div>
                <Badge variant={companyData?.status === 'active' ? 'default' : 'secondary'}>
                  {companyData?.status}
                </Badge>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Export/Import Data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex space-x-2">
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export Company Data
            </Button>
            <Button variant="outline">
              <Upload className="mr-2 h-4 w-4" />
              Import Data
            </Button>
          </div>
          <div className="text-sm text-muted-foreground">
            Export includes all projects, tasks, and settings. Import restores from backup.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Security & Compliance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Zap className="h-4 w-4 text-green-500" />
            <div>
              <div className="font-medium">2FA Enabled</div>
              <div className="text-sm text-muted-foreground">All users have 2FA enabled</div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Shield className="h-4 w-4 text-blue-500" />
            <div>
              <div className="font-medium">GDPR Compliant</div>
              <div className="text-sm text-muted-foreground">Data encrypted at rest and in transit</div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-yellow-500" />
            <div>
              <div className="font-medium">Audit Logs</div>
              <div className="text-sm text-muted-foreground">All actions logged for compliance</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanyManagement;